import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { NumericFormat } from 'react-number-format'

import MaxLengthMessage from '../../../features/components/MaxLengthMessage'

import { useDebounce } from '../../../hooks/useDebounce'

import { clearLSEPCharacters } from '../clearLSEPCharacters'

import useStyles from './styles'

function InputField({
  id,
  name,
  type,
  showError,
  resetError,
  decimalScale = 2,
  formikValue,
  autoComplete,
  setFieldValue,
  setFieldTouched,
  symbol,
  symbolPosition,
  showSymbol,
  enableReinitialize,
  formatType,
  onValueChange,
  maxLength,
  portalElement,
  thousandSeparator = true,
  showMaxLengthMessage = true,
  setOnEmptyChange,
  ...restFieldProps
}) {
  // We need to clear the value in some cases:
  // (e.g. user copy text with special character LSEP from text editor, and paste it to input)
  const cleanFormikValue = type !== 'number' ? clearLSEPCharacters(formikValue) : formikValue

  // format initial formikValue if it is not an empty string
  const [value, setValue] = useState(cleanFormikValue)
  const [valueReinitialized, setValueReinitialized] = useState(false)

  const hasValue = !!value
  const classes = useStyles({
    showError,
    hasValue,
    hasSymbol: !!symbol,
    symbolLength: symbol && symbol.length,
    showSymbol,
    symbolPosition
  })

  const handleValueChange = useCallback(
    ({ target }) => {
      const value = target.value

      setValue(value)
      resetError()

      // allow to handle some actions from outside
      onValueChange && onValueChange(value)

      // handle case when field is not focused but the value was changed(e.g. autocomplete)
      // in this case we can't rely on debounce update, because it happens too late
      const isFocused = document.activeElement === target

      if ((setOnEmptyChange && !value) || !isFocused) {
        const cleanTargetValue = type !== 'number' ? clearLSEPCharacters(value) : value
        setFieldValue(name, cleanTargetValue)
        setFieldTouched(name, true, false)
      }
    },
    [resetError, onValueChange, type, setFieldValue, setFieldTouched, name, setOnEmptyChange]
  )

  const handleNumberInputChange = useCallback(
    values => {
      // mockup data structure for handleValueChange and set unformatted value
      handleValueChange({ target: { value: values.value } })
    },
    [handleValueChange]
  )

  const handleBlur = useCallback(() => {
    setFieldTouched(name, true, false)
  }, [setFieldTouched, name])

  // To improve performance, we update formik only when the user has finished typing
  const debouncedValue = useDebounce(value, 100)
  useEffect(() => {
    // We need to clear the value in some cases:
    // (e.g. user copy text with special character LSEP from text editor, and paste it to input)
    // when type is number ignore clearing to avoid app crash
    const cleanDebouncedValue = type !== 'number' ? clearLSEPCharacters(debouncedValue) : debouncedValue

    if (cleanDebouncedValue !== debouncedValue) {
      // Set local state value if some character was found
      setValue(cleanDebouncedValue)
    }
    setFieldValue(name, cleanDebouncedValue)

    // reset reinitialized state for possible value resetting
    valueReinitialized && setValueReinitialized(false)
  }, [debouncedValue, setFieldValue, name, type, valueReinitialized])

  useEffect(() => {
    // we need to enable init value reinitialization for cases like edit form, when value is not available right away
    if (enableReinitialize && !valueReinitialized && cleanFormikValue !== value) {
      setValue(cleanFormikValue)
      setValueReinitialized(true)
    }
    // eslint-disable-next-line
  }, [cleanFormikValue, enableReinitialize, valueReinitialized, formatType])

  const inputProps = {
    ...restFieldProps,
    id,
    // name attribute is required
    name,
    value,
    onBlur: handleBlur,
    className: classes.input,
    autoComplete: autoComplete ? autoComplete : 'on'
  }

  return (
    <>
      {type === 'number' ? (
        <NumericFormat
          {...inputProps}
          thousandSeparator={thousandSeparator}
          decimalScale={decimalScale}
          allowedDecimalSeparators={['.']}
          onValueChange={handleNumberInputChange}
          valueIsNumericString
          maxLength={maxLength}
        />
      ) : (
        <input {...inputProps} type={type || 'text'} onChange={handleValueChange} maxLength={maxLength} />
      )}
      {symbol && <span className={classes.symbol}>{symbol}</span>}
      {maxLength &&
        portalElement &&
        showMaxLengthMessage &&
        createPortal(<MaxLengthMessage maxLength={maxLength} length={value.length} />, portalElement)}
    </>
  )
}

export default InputField
